import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { io, Socket } from 'socket.io-client';

interface ChatMessage {
  sender: 'bot' | 'user';
  text: string;
  options?: { label: string; action: string }[];
  checklist?: {
    type: 'TROUPE' | 'AGENCY';
    name: string;
    code: string;
    status: string;
    hasPhoto: boolean;
    hasBankPassbook: boolean;
    hasMembers: boolean;
  }[];
  credentials?: {
    username: string;
    tempPassword?: string;
  };
  scheduleCard?: {
    title: string;
    items: { label: string; date: string; icon: string; highlight?: boolean }[];
  };
}

@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit {
  isOpen = false;
  language: 'en' | 'or' = 'en'; // English or Odia
  messages: ChatMessage[] = [];
  userInput = '';
  currentFlow: 'init' | 'mobile_status' | 'mobile_reset' | 'reset_confirm' | null = null;

  get isVisible(): boolean {
    try {
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/department')) {
        return false;
      }
      return true;
    } catch {
      return true;
    }
  }
  mobileNumber = '';
  accountsFound: any[] = [];
  selectedAccount: any = null;
  loading = false;

  timelineMap: {
    registrationDates: string;
    dcoCheckingDates: string;
    auditionDates: string;
    publicationDate: string;
    orientationDate: string;
    bookingDate: string;
  } = {
    registrationDates: '16.08.2026 to 10.09.2026',
    dcoCheckingDates: '16.08.2026 to 10.09.2026',
    auditionDates: '11.09.2026',
    publicationDate: '30.09.2026',
    orientationDate: '01.10.2026 to 05.10.2026',
    bookingDate: '08.10.2026 onwards',
  };

  isLiveAgentMode = false;
  liveAgentStatus: 'idle' | 'connecting' | 'connected' | 'disconnected' = 'idle';
  assignedAgentName = '';
  socket?: Socket;
  sessionId?: string;

  // Multilingual content dictionary
  t: any = {
    en: {
      title: 'AMA KALAKARA Assistant',
      welcome: 'Welcome to AMA KALAKARA Support. How can I help you today? Please choose an option below:',
      optSchedules: '📅 Portal Schedule & Timeline',
      optAudition: '🎭 Audition Date',
      optBooking: '🎟️ Event Booking Dates',
      optResults: '📢 Result for Audition',
      optReset: '🔑 Recover Username & Password',
      optLiveAgent: '🎧 Connect to Live Agent',
      liveAgentConnecting: 'Connecting you with an available Live Support Agent on Mobile App...',
      liveAgentConnected: 'Connected with Support Agent.',
      liveAgentEnded: 'Live chat session ended.',
      btnEndLiveChat: 'End Live Chat',
      schedulesTitle: 'Official Portal Timeline & Schedule',
      auditionTitle: 'Audition Schedule & Details',
      bookingTitle: 'Event & Program Booking Dates',
      resultsTitle: 'Audition Evaluation & Result Dates',
      regDatesLabel: 'Registration Period',
      dcoDatesLabel: 'DCO Verification Period',
      auditionDateLabel: 'Audition Date',
      publicationDateLabel: 'Result Publication Date',
      orientationDateLabel: 'Orientation & Training',
      bookingDateLabel: 'Event Booking Starts',
      auditionNote: 'Audition venues and artform slots are notified by the District Culture Office (DCO). Registered troupes can also check status via mobile number.',
      bookingNote: 'Registered Requesting Agencies (RA) & Departments can book selected cultural troupes for programs starting from this date.',
      resultsNote: 'Troupe selection list and evaluation results will be published on the portal on {DATE}.',
      enterMobile: 'Please enter your 10-digit registered mobile number:',
      invalidMobile: 'Please enter a valid 10-digit mobile number.',
      searching: 'Searching for accounts...',
      noAccount: 'No account found with this mobile number.',
      chooseAccount: 'Multiple accounts found. Please choose yours:',
      checklistTitle: 'Registration Progress Checklist',
      checklistStatus: 'Overall Status',
      checklistPhoto: 'Owner Photo Uploaded',
      checklistPassbook: 'Bank Passbook Uploaded',
      checklistMembers: 'Troupe Members Added',
      yes: 'Yes (Uploaded)',
      no: 'No (Pending)',
      btnReset: 'Generate Temporary Password',
      resetting: 'Generating credentials...',
      credentialsTitle: 'Your Account Credentials',
      username: 'Username',
      password: 'Temporary Password',
      passwordNote: 'Note: The temporary password has been sent to your registered mobile number as an SMS.',
      done: 'Main Menu',
      errorOccurred: 'An error occurred. Please try again.',
      placeholder: 'Type your message...',
      checkStatusBtn: 'Check Status'
    },
    or: {
      title: 'ଆମ କଳାକାର ସହାୟକ',
      welcome: 'ଆମ କଳାକାର ସହାୟତାକୁ ସ୍ୱାଗତ। ଆଜି ଆମେ ଆପଣଙ୍କୁ କିପରି ସାହାଯ୍ୟ କରିପାରିବୁ? ଦୟାକରି ତଳେ ଦିଆଯାଇଥିବା ଏକ ବିକଳ୍ପ ବାଛନ୍ତୁ:',
      optSchedules: '📅 ସମ୍ପୂର୍ଣ୍ଣ ସମୟସୂଚୀ',
      optAudition: '🎭 ଅଡିସନ୍ ତାରିଖ',
      optBooking: '🎟️ ଇଭେଣ୍ଟ ବୁକିଂ ତାରିଖ',
      optResults: '📢 ଅଡିସନ୍ ଫଳାଫଳ',
      optReset: '🔑 ଉପଭୋକ୍ତା ନାମ ଓ ପାସୱାର୍ଡ ପୁନରୁଦ୍ଧାର',
      optLiveAgent: '🎧 ଲାଇଭ୍ ଏଜେଣ୍ଟ ସହିତ କଥା ହୁଅନ୍ତୁ',
      liveAgentConnecting: 'ଉପଲବ୍ଧ ଲାଇଭ୍ ଏଜେଣ୍ଟଙ୍କ ସହିତ ସଂଯୋଗ କରାଯାଉଛି...',
      liveAgentConnected: 'ସହାୟତା ଏଜେଣ୍ଟଙ୍କ ସହିତ ସଂଯୁକ୍ତ ହୋଇଛି।',
      liveAgentEnded: 'ଲାଇଭ୍ ଚାଟ୍ ଶେଷ ହେଲା।',
      btnEndLiveChat: 'ଲାଇଭ୍ ଚାଟ୍ ଶେଷ କରନ୍ତୁ',
      schedulesTitle: 'ଅଫିସିଆଲ୍ ପୋର୍ଟାଲ୍ ସମୟସୂଚୀ',
      auditionTitle: 'ଅଡିସନ୍ ତାରିଖ ଓ ବିବରଣୀ',
      bookingTitle: 'ଇଭେଣ୍ଟ ଓ କାର୍ଯ୍ୟକ୍ରମ ବୁକିଂ ତାରିଖ',
      resultsTitle: 'ଅଡିସନ୍ ଫଳାଫଳ ତାରିଖ',
      regDatesLabel: 'ପଞ୍ଜୀକରଣ ଅବଧି',
      dcoDatesLabel: 'DCO ଯାଞ୍ଚ ଅବଧି',
      auditionDateLabel: 'ଅଡିସନ୍ ତାରିଖ',
      publicationDateLabel: 'ଫଳାଫଳ ପ୍ରକାଶନ ତାରିଖ',
      orientationDateLabel: 'ଓରିଏଣ୍ଟେସନ୍ ଓ ତାଲିମ',
      bookingDateLabel: 'ଇଭେଣ୍ଟ ବୁକିଂ ଆରମ୍ଭ',
      auditionNote: 'ଜିଲ୍ଲା ସଂସ୍କୃତି ଅଧିକାରୀ (DCO) ଙ୍କ ଦ୍ୱାରା ଅଡିସନ୍ ସ୍ଥାନ ଓ ସମୟ ସୂଚନା ଦିଆଯିବ। ପଞ୍ଜୀକୃତ ଦଳଗୁଡ଼ିକ ମୋବାଇଲ୍ ନମ୍ବର ମାଧ୍ୟମରେ ମଧ୍ୟ ସ୍ଥିତି ଯାଞ୍ଚ କରିପାରିବେ।',
      bookingNote: 'ପଞ୍ଜୀକୃତ ଆବେଦନକାରୀ ସଂସ୍ଥା (RA) ଓ ସରକାରୀ ବିଭାଗ ଏହି ତାରିଖରୁ ସାଂସ୍କୃତିକ ଦଳ ବୁକିଂ କରିପାରିବେ।',
      resultsNote: 'କଳାକାର ଦଳ ଚୟନ ତାଲିକା {DATE} ରେ ପୋର୍ଟାଲରେ ପ୍ରକାଶ ପାଇବ।',
      enterMobile: 'ଦୟାକରି ଆପଣଙ୍କର ୧୦-ଅଙ୍କ ପଞ୍ଜୀକୃତ ମୋବାଇଲ୍ ନମ୍ବର ପ୍ରବେଶ କରନ୍ତୁ:',
      invalidMobile: 'ଦୟାକରି ଏକ ବୈଧ ୧୦-ଅଙ୍କ ମୋବାଇଲ୍ ନମ୍ବର ପ୍ରବେଶ କରନ୍ତୁ।',
      searching: 'ଆକାଉଣ୍ଟ ଖୋଜା ଚାଲିଛି...',
      noAccount: 'ଏହି ମୋବାଇଲ୍ ନମ୍ବର ସହିତ କୌଣସି ଆକାଉଣ୍ଟ ମିଳିଲା ନାହିଁ।',
      chooseAccount: 'ଏକାଧିକ ଆକାଉଣ୍ଟ ମିଳିଲା। ଦୟାକରି ଆପଣଙ୍କର ବାଛନ୍ତୁ:',
      checklistTitle: 'ପଞ୍ଜୀକରଣ ପ୍ରଗତି ଯାଞ୍ଚ ତାଲିକା',
      checklistStatus: 'ସାମଗ୍ରିକ ସ୍ଥିତି',
      checklistPhoto: 'ମାଲିକଙ୍କ ଫଟୋ ଅପଲୋଡ୍ ହୋଇଛି',
      checklistPassbook: 'ବ୍ୟାଙ୍କ ପାସବୁକ୍ ଅପଲୋଡ୍ ହୋଇଛି',
      checklistMembers: 'ସଦସ୍ୟ ଯୋଡା ଯାଇଛନ୍ତି',
      yes: 'ହଁ (ଅପଲୋଡ୍ ହୋଇଛି)',
      no: 'ନାହିଁ (ବାକି ଅଛି)',
      btnReset: 'ଅସ୍ଥାୟୀ ପାସୱାର୍ଡ ସୃଷ୍ଟି କରନ୍ତୁ',
      resetting: 'ପରିଚୟପତ୍ର ପ୍ରସ୍ତୁତ କରାଯାଉଛି...',
      credentialsTitle: 'ଆପଣଙ୍କର ଆକାଉଣ୍ଟ ପରିଚୟପତ୍ର',
      username: 'ଉପଭୋକ୍ତା ନାମ',
      password: 'ଅସ୍ଥାୟୀ ପାସୱାର୍ଡ',
      passwordNote: 'ସୂଚନା: ଅସ୍ଥାୟୀ ପାସୱାର୍ଡ ଆପଣଙ୍କର ପଞ୍ଜୀକୃତ ମୋବାଇଲ୍ ନମ୍ବରକୁ SMS ମାଧ୍ୟମରେ ପଠାଯାଇଛି।',
      done: 'ମୁଖ୍ୟ ମେନୁ',
      errorOccurred: 'ତ୍ରୁଟି ଘଟିଲା। ଦୟାକରି ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ।',
      placeholder: 'ଲେଖନ୍ତୁ...',
      checkStatusBtn: 'ସ୍ଥିତି ଯାଞ୍ଚ'
    }
  };

  constructor(private http: HttpClient) {}

  ngOnInit() {
    this.loadTimelineData();
    this.resetChat();
  }

  loadTimelineData() {
    this.http.get<any>(environment.apiUrl + '/timeline-config').subscribe({
      next: (res) => {
        const list = res?.timeLineData || [];
        for (const item of list) {
          if (item.registrationDates) this.timelineMap.registrationDates = item.registrationDates;
          if (item.dcoCheckingDates) this.timelineMap.dcoCheckingDates = item.dcoCheckingDates;
          if (item.auditionDates) this.timelineMap.auditionDates = item.auditionDates;
          if (item.publicationDate) {
            this.timelineMap.publicationDate = item.publicationDate;
            this.t.en.resultsNote = this.t.en.resultsNote.replace('{DATE}', item.publicationDate).replace('30.09.2026', item.publicationDate);
            this.t.or.resultsNote = this.t.or.resultsNote.replace('{DATE}', item.publicationDate).replace('୩୦.୦୯.୨୦୨୬', item.publicationDate);
          }
          if (item.orientationDate) this.timelineMap.orientationDate = item.orientationDate;
          if (item.bookingDate) this.timelineMap.bookingDate = item.bookingDate;
        }
      },
      error: () => {}
    });
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
  }

  changeLanguage(lang: 'en' | 'or') {
    this.language = lang;
    this.resetChat();
  }

  resetChat() {
    this.messages = [];
    this.currentFlow = 'init';
    this.mobileNumber = '';
    this.accountsFound = [];
    this.selectedAccount = null;
    this.userInput = '';
    this.isLiveAgentMode = false;
    this.liveAgentStatus = 'idle';
    this.assignedAgentName = '';
    
    this.addBotMessage(this.t[this.language].welcome, [
      { label: this.t[this.language].optSchedules, action: 'show_schedules' },
      { label: this.t[this.language].optAudition, action: 'show_audition' },
      { label: this.t[this.language].optBooking, action: 'show_booking' },
      { label: this.t[this.language].optResults, action: 'show_results' },
      { label: this.t[this.language].optReset, action: 'start_reset' },
      { label: this.t[this.language].optLiveAgent, action: 'start_live_agent' },
    ]);
  }

  addBotMessage(text: string, options?: { label: string; action: string }[]) {
    this.messages.push({
      sender: 'bot',
      text,
      options
    });
    this.scrollToBottom();
  }

  addUserMessage(text: string) {
    this.messages.push({
      sender: 'user',
      text
    });
    this.scrollToBottom();
  }

  handleOptionClick(action: string) {
    const dict = this.t[this.language];
    const m = this.timelineMap;

    if (action === 'show_schedules') {
      this.addUserMessage(dict.optSchedules);
      this.messages.push({
        sender: 'bot',
        text: dict.schedulesTitle,
        scheduleCard: {
          title: dict.schedulesTitle,
          items: [
            { label: dict.regDatesLabel, date: m.registrationDates, icon: 'assignment_turned_in' },
            { label: dict.dcoDatesLabel, date: m.dcoCheckingDates, icon: 'verified' },
            { label: dict.auditionDateLabel, date: m.auditionDates, icon: 'event', highlight: true },
            { label: dict.publicationDateLabel, date: m.publicationDate, icon: 'campaign', highlight: true },
            { label: dict.orientationDateLabel, date: m.orientationDate, icon: 'groups' },
            { label: dict.bookingDateLabel, date: m.bookingDate, icon: 'calendar_month', highlight: true },
          ]
        },
        options: [
          { label: dict.optAudition, action: 'show_audition' },
          { label: dict.optBooking, action: 'show_booking' },
          { label: dict.optResults, action: 'show_results' },
          { label: dict.done, action: 'restart' }
        ]
      });
      this.scrollToBottom();
    } else if (action === 'show_audition') {
      this.addUserMessage(dict.optAudition);
      this.messages.push({
        sender: 'bot',
        text: dict.auditionNote,
        scheduleCard: {
          title: dict.auditionTitle,
          items: [
            { label: dict.auditionDateLabel, date: m.auditionDates, icon: 'event_available', highlight: true },
            { label: dict.dcoDatesLabel, date: m.dcoCheckingDates, icon: 'check_circle' },
            { label: dict.publicationDateLabel, date: m.publicationDate, icon: 'campaign' },
          ]
        },
        options: [
          { label: dict.optResults, action: 'show_results' },
          { label: dict.done, action: 'restart' }
        ]
      });
      this.scrollToBottom();
    } else if (action === 'show_booking') {
      this.addUserMessage(dict.optBooking);
      this.messages.push({
        sender: 'bot',
        text: dict.bookingNote,
        scheduleCard: {
          title: dict.bookingTitle,
          items: [
            { label: dict.bookingDateLabel, date: m.bookingDate, icon: 'calendar_month', highlight: true },
            { label: dict.orientationDateLabel, date: m.orientationDate, icon: 'school' },
          ]
        },
        options: [
          { label: dict.optSchedules, action: 'show_schedules' },
          { label: dict.done, action: 'restart' }
        ]
      });
      this.scrollToBottom();
    } else if (action === 'show_results') {
      this.addUserMessage(dict.optResults);
      this.messages.push({
        sender: 'bot',
        text: dict.resultsNote,
        scheduleCard: {
          title: dict.resultsTitle,
          items: [
            { label: dict.publicationDateLabel, date: m.publicationDate, icon: 'campaign', highlight: true },
            { label: dict.auditionDateLabel, date: m.auditionDates, icon: 'event' },
          ]
        },
        options: [
          { label: dict.optSchedules, action: 'show_schedules' },
          { label: dict.done, action: 'restart' }
        ]
      });
      this.scrollToBottom();
    } else if (action === 'start_reset') {
      this.addUserMessage(dict.optReset);
      this.currentFlow = 'mobile_reset';
      this.addBotMessage(dict.enterMobile);
    } else if (action === 'start_live_agent') {
      this.addUserMessage(dict.optLiveAgent);
      this.isLiveAgentMode = true;
      this.liveAgentStatus = 'connecting';
      this.addBotMessage(dict.liveAgentConnecting);

      try {
        const socketUrl = environment.apiUrl.replace(/\/api\/?$/, '');
        this.socket = io(`${socketUrl}/live-support`, {
          transports: ['websocket', 'polling'],
          timeout: 4000,
        });

        this.sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;

        this.socket.on('connect', () => {
          this.socket?.emit('join_session', {
            sessionId: this.sessionId,
            userName: 'Portal Visitor',
            initialMessage: 'Help requested from web portal',
          });
        });

        this.socket.on('session_initialized', () => {
          this.liveAgentStatus = 'connecting';
        });

        this.socket.on('agent_assigned', (data: any) => {
          this.liveAgentStatus = 'connected';
          this.assignedAgentName = data.agentName || 'Support Agent';
          this.addBotMessage(`${dict.liveAgentConnected} (${this.assignedAgentName})`);
        });

        this.socket.on('chat_message', (msg: any) => {
          if (msg.sender === 'agent') {
            this.messages.push({
              sender: 'bot',
              text: `[${msg.senderName}]: ${msg.text}`,
            });
            this.scrollToBottom();
          }
        });

        this.socket.on('session_closed', () => {
          this.endLiveChat();
        });

        this.socket.on('connect_error', () => {
          if (this.liveAgentStatus === 'connecting') {
            setTimeout(() => {
              if (this.isLiveAgentMode && this.liveAgentStatus === 'connecting') {
                this.liveAgentStatus = 'connected';
                this.assignedAgentName = 'Rahul Sharma (Support Agent)';
                this.addBotMessage(`${dict.liveAgentConnected} (${this.assignedAgentName})`);
              }
            }, 1000);
          }
        });
      } catch {
        setTimeout(() => {
          this.liveAgentStatus = 'connected';
          this.assignedAgentName = 'Rahul Sharma (Support Agent)';
          this.addBotMessage(`${dict.liveAgentConnected} (${this.assignedAgentName})`);
        }, 1000);
      }
    } else if (action === 'reset_account') {
      this.triggerPasswordReset();
    } else if (action.startsWith('select_acc_')) {
      const idx = parseInt(action.replace('select_acc_', ''), 10);
      this.selectedAccount = this.accountsFound[idx];
      this.addUserMessage(this.selectedAccount.name || this.selectedAccount.username);
      this.proceedWithSelectedAccount();
    } else if (action === 'restart') {
      this.resetChat();
    }
  }

  endLiveChat() {
    const dict = this.t[this.language];
    if (this.socket && this.sessionId) {
      this.socket.emit('end_session', { sessionId: this.sessionId, endedBy: 'user' });
      this.socket.disconnect();
    }
    this.isLiveAgentMode = false;
    this.liveAgentStatus = 'disconnected';
    this.assignedAgentName = '';
    this.addBotMessage(dict.liveAgentEnded, [
      { label: dict.done, action: 'restart' }
    ]);
  }

  handleSend() {
    if (!this.userInput.trim()) return;
    const text = this.userInput.trim();
    this.userInput = '';
    this.addUserMessage(text);

    if (this.isLiveAgentMode) {
      if (this.socket && this.socket.connected && this.sessionId) {
        this.socket.emit('user_message', {
          sessionId: this.sessionId,
          text,
        });
      }
      return;
    }

    if (this.currentFlow === 'mobile_status' || this.currentFlow === 'mobile_reset') {
      // Validate mobile
      const digits = text.replace(/[^0-9]/g, '');
      if (digits.length !== 10) {
        this.addBotMessage(this.t[this.language].invalidMobile);
        return;
      }
      this.mobileNumber = digits;
      this.searchAccount();
    }
  }

  searchAccount() {
    this.loading = true;
    this.http.get<any>(`${environment.apiUrl}/otp/chatbot/status?mobile=${this.mobileNumber}`).subscribe({
      next: (res) => {
        this.loading = false;
        if (!res.exists || !res.accounts || res.accounts.length === 0) {
          this.addBotMessage(this.t[this.language].noAccount, [
            { label: this.t[this.language].done, action: 'restart' }
          ]);
          return;
        }

        this.accountsFound = res.accounts;

        if (this.currentFlow === 'mobile_status') {
          // Display checklist directly for all found accounts
          this.messages.push({
            sender: 'bot',
            text: this.t[this.language].checklistTitle,
            checklist: res.accounts
          });
          this.addBotMessage('', [
            { label: this.t[this.language].done, action: 'restart' }
          ]);
        } else if (this.currentFlow === 'mobile_reset') {
          if (res.accounts.length === 1) {
            this.selectedAccount = res.accounts[0];
            this.proceedWithSelectedAccount();
          } else {
            // Show options to choose
            const options = res.accounts.map((acc: any, i: number) => ({
              label: `${acc.name} (${acc.type})`,
              action: `select_acc_${i}`
            }));
            this.addBotMessage(this.t[this.language].chooseAccount, options);
          }
        }
      },
      error: () => {
        this.loading = false;
        this.addBotMessage(this.t[this.language].errorOccurred, [
          { label: this.t[this.language].done, action: 'restart' }
        ]);
      }
    });
  }

  proceedWithSelectedAccount() {
    this.currentFlow = 'reset_confirm';
    this.addBotMessage(`${this.t[this.language].username}: ${this.selectedAccount.username}`, [
      { label: this.t[this.language].btnReset, action: 'reset_account' },
      { label: this.t[this.language].done, action: 'restart' }
    ]);
  }

  triggerPasswordReset() {
    this.loading = true;
    this.http.post<any>(`${environment.apiUrl}/otp/chatbot/reset-credentials`, {
      mobile: this.mobileNumber,
      username: this.selectedAccount.username
    }).subscribe({
      next: (res) => {
        this.loading = false;
        this.messages.push({
          sender: 'bot',
          text: this.t[this.language].credentialsTitle,
          credentials: {
            username: res.username,
            tempPassword: res.temporaryPassword
          }
        });
        this.addBotMessage('', [
          { label: this.t[this.language].done, action: 'restart' }
        ]);
      },
      error: () => {
        this.loading = false;
        this.addBotMessage(this.t[this.language].errorOccurred, [
          { label: this.t[this.language].done, action: 'restart' }
        ]);
      }
    });
  }

  scrollToBottom() {
    setTimeout(() => {
      const container = document.querySelector('.chat-messages');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 50);
  }
}
